import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";

export type StockDropdownColumn = { key: string; label: string };

/** Resolve the ordered compact columns to show in the product dropdown from
 * the company's stockCustomFields config. A column is any field with
 * `showInDropdown === true`; columns are ordered by `index` (missing index
 * sorts last). Duplicate keys are dropped (first occurrence wins) so a
 * misconfigured admin config never yields repeated dropdown columns / duplicate
 * React keys, and a missing/non-string label falls back to the key. Single
 * source of truth — replaces the hardcoded j1..j5 list. Mirrors the AI-side
 * `resolve_stock_columns` (ai/agent_system/tools/domain/_stock_columns.py) so
 * the dropdown and the agent agree on which columns exist. */
export function resolveStockDropdownColumns(
  fields?: CustomFieldDefinition[],
): StockDropdownColumn[] {
  if (!fields) return [];
  const ordered = fields
    .filter((f) => f.showInDropdown === true && typeof f.key === "string")
    .map((f, i) => ({ field: f, i }))
    .sort((a, b) => {
      // Only a finite numeric `index` participates in ordering; anything else
      // (missing, or a malformed string/NaN in the unvalidated admin JSON)
      // sorts last. Mirrors the AI-side `resolve_stock_columns`, which treats a
      // non-numeric index as "last" — a bare `ai - bi` would otherwise let JS
      // coerce a string `"1"` into the ordering and diverge from the agent.
      const ai = Number.isFinite(a.field.index)
        ? (a.field.index as number)
        : Number.POSITIVE_INFINITY;
      const bi = Number.isFinite(b.field.index)
        ? (b.field.index as number)
        : Number.POSITIVE_INFINITY;
      if (ai !== bi) return ai - bi;
      return a.i - b.i; // stable for equal/missing index
    });
  const seen = new Set<string>();
  const resolved: StockDropdownColumn[] = [];
  for (const { field } of ordered) {
    if (seen.has(field.key)) continue; // first wins — no double-counting
    seen.add(field.key);
    const label = typeof field.label === "string" ? field.label : field.key;
    resolved.push({ key: field.key, label });
  }
  return resolved;
}
